<template>
  <div class="flex gap-sm border border-solid border-gray-200 rounded-md p-sm" :class="{ 'opacity-75': isInactive }">
    <!-- Date tile: strip-picker DNA, tinted green once the lesson is complete -->
    <div class="flex flex-column align-items-center justify-center flex-shrink-0 rounded-sm" :class="tileClasses" style="width: 56px; height: 56px;">
      <span class="upper-label text-10" style="line-height: 1.2;">{{ eventDayOfWeek }}</span>
      <span class="text-18 font-heavy" style="line-height: 1.2;" :class="{ 'text-strikethrough': isInactive }">{{ eventDayNumber }}</span>
      <span class="text-10" style="line-height: 1.2;">{{ eventMonthShort }}</span>
    </div>

    <div class="flex-1" style="min-width: 0;">
      <div class="flex align-items-start justify-space-between gap-x-sm">
        <p class="m-y-none font-semi pl-slot-time" :class="{ 'text-strikethrough text-gray-500': isInactive }">
          {{ eventTime }} <span class="font-normal text-gray-600">· {{ durationMinutes }} min</span>
        </p>
        <span v-if="statusLabel" class="pl-slot-badge flex-shrink-0" :class="badgeClasses">{{ statusLabel }}</span>
      </div>
      <p class="m-y-none text-14 text-gray-600 break-words">{{ participantSummary }}</p>

      <div class="flex align-items-center justify-space-between gap-x-sm m-t-xs">
        <a v-if="hasNotes" class="text-14 font-semi text-blue-600 cursor-pointer" @click="showNotes = !showNotes">
          <i class="m-r-xxs" :class="showNotes ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>Lesson notes
        </a>
        <em v-else class="text-14 text-gray-500">No lesson notes yet</em>
        <a v-if="!forAppEmbed" :href="eventDetailUrl" class="text-14 font-semi text-blue-600 flex-shrink-0">Details</a>
        <a v-else-if="firstEvent" class="text-14 font-semi text-blue-600 flex-shrink-0 cursor-pointer" @click="pushEventDetail">Details</a>
      </div>
      <div v-if="hasNotes && showNotes" class="private-lesson-notes bg-gray-50 rounded-sm p-sm m-t-xs">
        <div v-html="privateLesson.notes"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { appEmbedMix } from './mixins/app_embed_mix';

// One private lesson in the wrestler/parent-facing list (wrestler profile tab
// and the app embed): date tile on the left, time + participants + status
// badge on the right, notes behind a disclosure so rich notes don't stretch
// the list. Status reads through the current wrestler's own booking.
export default {
  name: 'PrivateLessonCard',
  mixins: [appEmbedMix],
  props: {
    privateLesson: {
      type: Object,
      required: true,
    },
    // Whose booking status the card badges — without one of these the badge
    // silently vanishes. Single-wrestler surfaces (the wrestler profile tab)
    // pass wrestlerId; the family booking list passes wrestlerIds and the
    // badge reads the first family booking on the lesson.
    wrestlerId: {
      type: Number,
      default: null,
    },
    wrestlerIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showNotes: false,
    };
  },
  computed: {
    firstEventBookings() {
      return this.firstEvent ? this.firstEvent.event_bookings : [];
    },
    coachNames() {
      return this.firstEventBookings
        .filter((booking) => booking.profile_type === 'CoachProfile')
        .map((booking) => booking.profile.display_name)
        .join(', ');
    },
    confirmedOrCompletedWrestlerNames() {
      return this.firstEventBookings
        .filter((booking) => booking.profile_type === 'WrestlerProfile' && (booking.status === 'confirmed' || booking.status === 'completed'))
        .map((booking) => booking.profile.display_name)
        .join(', ');
    },
    participantSummary() {
      const wrestlers = this.confirmedOrCompletedWrestlerNames || 'No wrestlers';
      return this.coachNames ? `${this.coachNames} · ${wrestlers}` : wrestlers;
    },
    badgedWrestlerIds() {
      return this.wrestlerId ? [this.wrestlerId] : this.wrestlerIds;
    },
    currentWrestlerEventBooking() {
      return this.firstEventBookings.find((booking) => booking.profile_type === 'WrestlerProfile' && this.badgedWrestlerIds.includes(booking.profile_id));
    },
    bookingStatus() {
      return this.currentWrestlerEventBooking ? this.currentWrestlerEventBooking.status : null;
    },
    // Deliberately NOT privateLessonStatusMix: that mixin labels *slot* status
    // from the coach's side ('Open'/'Booked'), while this badge is the family's
    // own booking ('Pending'/'Confirmed'). Colors for the shared statuses stay
    // aligned with the mixin; completed is green here (a finished lesson is a
    // positive on the family's card, not an archived slot).
    statusLabel() {
      switch (this.bookingStatus) {
        case 'confirmed': return 'Confirmed';
        case 'completed': return 'Completed';
        case 'cancelled': return 'Cancelled';
        case 'pending': return 'Pending';
        case 'invited': return 'Invited';
        case 'declined': return 'Declined';
        default: return null;
      }
    },
    badgeClasses() {
      switch (this.bookingStatus) {
        case 'confirmed': return 'bg-blue-50 text-blue-700';
        case 'completed': return 'bg-green-50 text-green-700';
        case 'invited': return 'bg-yellow-50 text-yellow-700';
        case 'cancelled':
        case 'declined': return 'bg-red-50 text-red-700';
        default: return 'bg-gray-100 text-gray-600';
      }
    },
    isInactive() {
      return this.bookingStatus === 'cancelled' || this.bookingStatus === 'declined';
    },
    tileClasses() {
      if (this.bookingStatus === 'completed') {
        return 'bg-green-50 text-green-700';
      }
      if (this.isInactive) {
        return 'bg-gray-100 text-gray-400';
      }
      return 'bg-gray-100 text-gray-600';
    },
    hasNotes() {
      return Boolean(this.privateLesson.notes);
    },
    firstEvent() {
      return this.privateLesson.events && this.privateLesson.events.length > 0
        ? this.privateLesson.events[0]
        : null;
    },
    eventStartAt() {
      return this.firstEvent ? this.firstEvent.start_at : null;
    },
    eventEndAt() {
      return this.firstEvent ? this.firstEvent.end_at : null;
    },
    eventDayOfWeek() {
      if (!this.eventStartAt) return '';
      return new Date(this.eventStartAt).toLocaleDateString('en-US', { weekday: 'short' });
    },
    eventDayNumber() {
      if (!this.eventStartAt) return '';
      return new Date(this.eventStartAt).getDate();
    },
    // "Jun" for this year's lessons, "Jun '24" once the year matters
    eventMonthShort() {
      if (!this.eventStartAt) return '';
      const startDate = new Date(this.eventStartAt);
      const month = startDate.toLocaleDateString('en-US', { month: 'short' });
      const year = startDate.getFullYear();
      if (year === new Date().getFullYear()) {
        return month;
      }
      return `${month} ’${String(year).slice(-2)}`;
    },
    eventTime() {
      if (!this.eventStartAt || !this.eventEndAt) return '';
      const startTime = new Date(this.eventStartAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      const endTime = new Date(this.eventEndAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
      return `${startTime} – ${endTime}`;
    },
    durationMinutes() {
      if (!this.eventStartAt || !this.eventEndAt) return 0;
      return Math.round((new Date(this.eventEndAt) - new Date(this.eventStartAt)) / 60000);
    },
    eventDetailUrl() {
      return this.firstEvent ? `/events/${this.firstEvent.id}` : '#';
    },
  },
  methods: {
    // In the embed, details is a pushed native screen (cancel/edit live
    // there), same target the agenda slot rows push
    pushEventDetail() {
      this.askAppToPush(`${window.location.origin}/app_embeds/events/${this.firstEvent.id}?use_native=false`, 'View Lesson');
    },
  },
};
</script>
